// Shared broadcast history table. One component, two callers:
//   • Platform admin (/admin/broadcasts) — shows the duplicate/delete
//     action menu and links the row to the admin detail route.
//   • Group leaders (in GroupBroadcastsSection) — shows the same row data
//     read-only; no action menu (those mutations are admin-only) and no
//     detail link (the detail route is admin-only too).
//
// The filter-tab + pagination URLs are scope-aware via `buildHref`/`buildUrl`
// callbacks so the same component can drive route navigation on either
// surface without knowing the URL shape.

import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Body,
  DataTable,
  Icon,
  IconTile,
  Label,
  PagedTable,
  Row,
  Stack,
  TabBar,
  type ColumnDef,
  type CursorPage,
  type TabBarItem,
  type Tone,
} from '@leanin/ui';
import { I } from '@leanin/ui';

import { BroadcastStatusPill, type BroadcastStatus } from '@/components/admin/BroadcastStatusPill';
import { BroadcastActionMenu } from '@/components/admin/BroadcastActionMenu';
import { ErrorState, EmptyState } from '@leanin/ui';
import type { DateFormatters } from '@/lib/format-date';
import { useFormatDate } from '@/lib/format-date.client';
import {
  useBroadcasts,
  useBroadcastStatusCounts,
  type BroadcastListStatus,
  type BroadcastScope,
  type BroadcastsPageParams,
} from '@/lib/queries/broadcasts';
import {
  useDeleteBroadcast,
  useDuplicateBroadcast,
} from '@/lib/mutations/admin';
import { audienceLabel } from './audience-label';

type BroadcastRow = NonNullable<
  ReturnType<typeof useBroadcasts>['data']
>['items'][number];

type Props = {
  readonly scope: BroadcastScope;
  readonly listParams: BroadcastsPageParams;
  readonly active: BroadcastListStatus;
  readonly cursor: string | undefined;
  // Build the href for a filter tab (status='all' means "no filter").
  readonly buildTabHref: (status: BroadcastListStatus) => string;
  // Build the href for a pagination cursor (null = first page).
  readonly buildPageHref: (cursor: string | null) => string;
  // Key used by PagedTable's history hint — distinct per surface.
  readonly historyKey: string;
};

const TILE_TONE: Record<BroadcastStatus, Tone> = {
  draft: 'neutral',
  scheduled: 'navy',
  sent: 'emerald',
};

function whenText(b: BroadcastRow, fmt: DateFormatters): string {
  switch (b.status) {
    case 'sent':
      return b.sentAt ? fmt.medium(b.sentAt) : '—';
    case 'scheduled':
      return b.scheduledSendAt ? fmt.medium(b.scheduledSendAt) : '—';
    case 'draft':
    default:
      return '—';
  }
}

// Opens and clicks are email signals; a Direct message has neither.
function showsEmailMetrics(b: BroadcastRow): boolean {
  return b.status === 'sent' && b.sendVia !== 'dm';
}

export function BroadcastsList({
  scope,
  listParams,
  active,
  cursor,
  buildTabHref,
  buildPageHref,
  historyKey,
}: Props) {
  const navigate = useNavigate();
  const fmt = useFormatDate();
  const tAudience = useTranslations('Admin.broadcasts.audience');
  const tBroadcasts = useTranslations('Admin.broadcasts');
  const broadcastsQ = useBroadcasts(scope, listParams);
  const countsQ = useBroadcastStatusCounts(scope);
  const duplicate = useDuplicateBroadcast();
  const del = useDeleteBroadcast();

  // Group leaders get the same per-row actions admins do — duplicate/delete
  // routes already gate on Path B `broadcastRules.updatable`, so leader
  // authorization is enforced server-side. Step-up MFA still applies via the
  // route middleware if the leader's session isn't fresh.
  const showActions = scope.kind === 'platform' || scope.kind === 'group';

  if (broadcastsQ.isError) {
    return (
      <ErrorState
        title="We couldn't load broadcasts."
        body="Try again in a moment."
      />
    );
  }
  const data = broadcastsQ.data;
  if (data === undefined) return null;
  const counts = countsQ.data as Record<string, number> | undefined;

  const pickCount = (key: string): number | undefined =>
    counts ? counts[key] : undefined;

  // Server-side status filter. Each tab drives a URL navigation via
  // buildTabHref so the filter survives reloads and back/forward. Counts
  // (when loaded) render inline via TabBar's native count slot.
  const STATUS_TABS: ReadonlyArray<{ id: BroadcastListStatus; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'draft', label: 'Draft' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'sent', label: 'Sent' },
  ];

  const filterItems: ReadonlyArray<TabBarItem> = STATUS_TABS.map((t) => {
    const count = t.id === 'all' ? undefined : pickCount(t.id);
    return typeof count === 'number'
      ? { id: t.id, label: t.label, count }
      : { id: t.id, label: t.label };
  });

  const filter = (
    <TabBar
      items={filterItems}
      value={active}
      onChange={(id) => {
        void navigate({ to: buildTabHref(id as BroadcastListStatus) });
      }}
    />
  );

  const broadcasts = data.items;
  const page: CursorPage<BroadcastRow> = {
    items: [...broadcasts],
    nextCursor: data.nextCursor,
    pageSize: data.pageSize,
  };
  const currentCursor = cursor ?? null;

  const columns: ColumnDef<BroadcastRow>[] = [
    {
      id: 'subject',
      header: showActions ? 'Subject & audience' : 'Subject',
      // Identity column: on mobile it becomes the card's full-width lead
      // block (no inline label) — the icon + subject + audience already
      // say what the row is.
      meta: { mobilePrimary: true },
      cell: ({ row }) => {
        const b = row.original;
        const statusKey: BroadcastStatus = b.status;
        const tileTone = TILE_TONE[statusKey];
        const recipientCount = b.recipientCount ?? 0;
        const recipients = tAudience('recipientCount', { count: recipientCount });
        // Direct message rows name their channel; email is the default and
        // goes unlabelled.
        const channel = b.sendVia === 'dm' ? ` · ${tBroadcasts('sendVia.dm')}` : '';
        return (
          <Row gap="md" align="center" style={{ minWidth: 0 }}>
            <IconTile tone={tileTone} size={36}>
              <Icon size={18}>{b.sendVia === 'dm' ? I.chat : I.mail}</Icon>
            </IconTile>
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Label size="lg">{b.subject}</Label>
              <Body size="sm" color="mute">
                {showActions
                  ? `${audienceLabel(b.audienceSpec, tAudience)} · ${recipients}${channel}`
                  : `${recipients}${channel}`}
              </Body>
            </Stack>
          </Row>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      meta: { width: 160 },
      cell: ({ row }) => (
        <BroadcastStatusPill
          status={row.original.status}
          scheduledSendAt={row.original.scheduledSendAt}
        />
      ),
    },
    {
      id: 'when',
      header: 'Sent / scheduled',
      meta: { width: 200 },
      cell: ({ row }) => {
        const b = row.original;
        return (
          <Stack gap="xs">
            <Body size="sm">{whenText(b, fmt)}</Body>
            {showsEmailMetrics(b) ? (
              <Body size="sm" color="mute">
                Opens {b.openCount.toLocaleString()} · Clicks{' '}
                {b.clickCount.toLocaleString()}
              </Body>
            ) : null}
          </Stack>
        );
      },
    },
    ...(showActions
      ? [
          {
            id: 'actions',
            // Unlabeled, matching the members-table actions convention —
            // and on mobile the empty label makes the buttons trail the
            // card without an "Actions" prefix.
            header: '',
            // Wide enough to fit the split-button's `Continue editing` /
            // `Details` label plus the chevron-more affordance without
            // clipping at common viewport widths.
            meta: { width: 220, align: 'end' as const, nowrap: true },
            cell: ({ row }) => (
              // DataTable declares `meta.align: 'end'` but never actually
              // applies it to cell content (right-aligns the header only).
              // Wrap in a full-width Row that pushes children to the end so
              // the split buttons sit under the Actions header instead of
              // hugging the column's left edge.
              <Row justify="flex-end" style={{ width: '100%' }}>
                <BroadcastActionMenu
                  broadcastId={row.original.id}
                  subject={row.original.subject}
                  status={row.original.status}
                  primaryHref={buildRowHref(row.original)}
                  locked={isLocked(row.original)}
                  onDuplicate={async (id) => {
                    await duplicate.mutateAsync(id);
                  }}
                  onDelete={async (id) => {
                    await del.mutateAsync(id);
                  }}
                />
              </Row>
            ),
          } satisfies ColumnDef<BroadcastRow>,
        ]
      : []),
  ];

  // Each scope owns its own broadcast-detail route (server enforces that
  // group leaders only see their own group's broadcasts). Skip the click
  // if it bubbled up from an interactive element so BroadcastActionMenu
  // and any future inline links keep working.
  //
  // "Locked" rows — sent OR scheduled whose send time has elapsed —
  // route to the read-only detail page; everything else routes to the
  // editor. A past-due scheduled broadcast is mid/awaiting dispatch and is
  // no longer safely editable, so it opens read-only like a sent one.
  const isLocked = (b: BroadcastRow): boolean => {
    if (b.status === 'sent') return true;
    if (b.status !== 'scheduled') return false;
    return (
      typeof b.scheduledSendAt === 'string' &&
      Date.parse(b.scheduledSendAt) <= Date.now()
    );
  };
  const buildRowHref = (b: BroadcastRow): string => {
    const base =
      scope.kind === 'platform'
        ? `/admin/broadcasts/${b.id}`
        : scope.groupKind === 'circle'
          ? `/circles/${scope.groupId}/admin/broadcasts/${b.id}`
          : scope.groupKind === 'network'
            ? `/networks/${scope.groupId}/admin/broadcasts/${b.id}`
            : `/groups/${scope.groupId}/admin/broadcasts/${b.id}`;
    return isLocked(b) ? base : `${base}/edit`;
  };

  const handleRowClick = (
    row: unknown,
    e: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, a, [role="menuitem"]')) return;
    const b = row as BroadcastRow;
    void navigate({ to: buildRowHref(b) });
  };

  if (broadcasts.length === 0 && !cursor) {
    return (
      <Stack gap="md">
        {filter}
        <EmptyState
          icon="mail"
          tone="burgundy"
          title="No broadcasts yet."
          body={
            scope.kind === 'platform'
              ? 'Use New broadcast to compose one.'
              : 'Use the composer above to send your first one.'
          }
        />
      </Stack>
    );
  }

  return (
    <PagedTable
      page={page}
      currentCursor={currentCursor}
      historyKey={historyKey}
      onPageChange={(cur) => navigate({ to: buildPageHref(cur) })}
    >
      {(items) => (
        <DataTable data={[...items]} columns={columns}>
          <DataTable.Toolbar>{filter}</DataTable.Toolbar>
          <DataTable.Header />
          <DataTable.Body onRowClick={handleRowClick} />
        </DataTable>
      )}
    </PagedTable>
  );
}
